

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
// Window-target pop listeners run in registration order in Chrome, including
// capture listeners. Install before the router mounts; only current dirty forms
// delegate here. Mounting a late listener lets the router retire the form first.
const dirtyPopGuards = new Set<(event: PopStateEvent) => void>();
const guardHistoryPop = (event: PopStateEvent) => {
  for (const guard of dirtyPopGuards) {
    guard(event);
    if (event.cancelBubble) break;
  }
};
if (typeof window !== "undefined") {
  window.addEventListener("popstate", guardHistoryPop, true);
  import.meta.hot?.dispose(() => {
    window.removeEventListener("popstate", guardHistoryPop, true);
    dirtyPopGuards.clear();
  });
}

type UseFormSafetyOptions = {
  isDirty: boolean;
  onDiscard?: () => void;
  enableNavigationGuard?: boolean;
};

/**
 * Spread straight onto `@a2z/ui-v2`'s `ConfirmDialog`, so the field names are
 * its prop names. Discard is the destructive side (`onConfirm`); continuing to
 * edit is the safe one, and it is what Esc and a backdrop click resolve to.
 */
type DiscardDialogState = {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function useFormSafety({
  isDirty,
  onDiscard,
  enableNavigationGuard = true,
}: UseFormSafetyOptions) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const pendingHistoryPopRef = useRef(false);

  const allowNextPopRef = useRef(false);
  const guardedUrlRef = useRef<string | null>(null);
  const guardedHistoryStateRef = useRef<unknown>(null);

  const promptDiscard = useCallback(
    (action?: () => void, options: { allowHistoryPop?: boolean } = {}) => {
      if (!isDirty) {
        action?.();
        return;
      }
      pendingActionRef.current = action ?? null;
      pendingHistoryPopRef.current = Boolean(options.allowHistoryPop);
      setIsDialogOpen(true);
    },
    [isDirty]
  );

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!isDirty) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
      return "";
    },
    [isDirty]
  );

  useEffect(() => {
    if (!enableNavigationGuard || !isDirty || typeof window === "undefined") {
      return;
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [enableNavigationGuard, handleBeforeUnload, isDirty]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (isDirty) {
      if (!guardedUrlRef.current) {
        guardedUrlRef.current = window.location.href;
        guardedHistoryStateRef.current = window.history.state;
      }
      return;
    }
    guardedUrlRef.current = window.location.href;
    guardedHistoryStateRef.current = window.history.state;
  }, [isDirty]);

  useLayoutEffect(() => {
    if (!enableNavigationGuard || !isDirty || typeof window === "undefined") {
      return;
    }

    const handlePopState = (event: PopStateEvent) => {
      if (allowNextPopRef.current) {
        allowNextPopRef.current = false;
        return;
      }
      // The early dispatcher must stop the router until the discard decision.
      event.stopImmediatePropagation();
      const guardedUrl = guardedUrlRef.current ?? window.location.href;
      window.history.pushState(guardedHistoryStateRef.current, "", guardedUrl);
      promptDiscard(() => window.history.back(), { allowHistoryPop: true });
    };

    dirtyPopGuards.add(handlePopState);
    return () => { dirtyPopGuards.delete(handlePopState); };
  }, [enableNavigationGuard, isDirty, promptDiscard]);

  const handleDiscardConfirmed = useCallback(() => {
    onDiscard?.();
    if (pendingHistoryPopRef.current) allowNextPopRef.current = true;
    pendingActionRef.current?.();
    pendingActionRef.current = null;
    pendingHistoryPopRef.current = false;
    setIsDialogOpen(false);
  }, [onDiscard]);

  const handleDiscardCancelled = useCallback(() => {
    pendingActionRef.current = null;
    pendingHistoryPopRef.current = false;
    setIsDialogOpen(false);
  }, []);

  return {
    promptDiscard,
    discardDialogState: {
      open: isDialogOpen,
      onConfirm: handleDiscardConfirmed,
      onCancel: handleDiscardCancelled,
    } as DiscardDialogState,
  };
}
